import type { VoucherCrud } from '@diamond/shared/schema/voucher';

type VoucherCreateBody = VoucherCrud['create']['body'];
type VoucherUpdateBody = VoucherCrud['update']['body'];
type VoucherListItem = VoucherCrud['list']['response']['data'][number];
type VoucherRewardConfigurationSchema =
  VoucherCreateBody['reward_configuration'];
type VoucherTargetConfigurationSchema =
  VoucherCreateBody['target_configuration'];
type VoucherAcquisitionConfigurationSchema =
  VoucherCreateBody['acquisition_configuration'];
type VoucherConditionConfigurationSchema =
  VoucherCreateBody['condition_configuration'];

// ---------------------------------------------------------------------------
// Backoffice types derived from shared schema (admin usage)
// ---------------------------------------------------------------------------

export type BackofficeVoucherListResponse = VoucherCrud['list']['response'];
export type BackofficeVoucherListQuery = VoucherCrud['list']['query'];
export type BackofficeVoucherListItem =
  BackofficeVoucherListResponse['data'][number];

export type BackofficeVoucherProductListResponse =
  VoucherCrud['getProductList']['response'];
export type BackofficeVoucherProductListQuery =
  VoucherCrud['getProductList']['query'];
export type BackofficeVoucherProductListItem =
  BackofficeVoucherProductListResponse['data'][number];

export type BackofficeVoucherCreateRequest = VoucherCrud['create']['body'];
export type BackofficeVoucherCreateResponse = VoucherCrud['create']['response'];

export type BackofficeVoucherUpdateRequest = VoucherCrud['update']['body'];
export type BackofficeVoucherUpdateResponse = VoucherCrud['update']['response'];

export type BackofficeVoucherDetailResponse =
  VoucherCrud['getById']['response'];

export type BackofficeVoucherCheckCodeRequest =
  VoucherCrud['checkCode']['body'];
export type BackofficeVoucherCheckCodeResponse =
  VoucherCrud['checkCode']['response'];

export type BackofficeVoucherUpdateStatusRequest =
  VoucherCrud['updateStatus']['body'];
export type BackofficeVoucherUpdateStatusResponse =
  VoucherCrud['updateStatus']['response'];

export type BackofficeVoucherUpdateDisplayRequest =
  VoucherCrud['updateDisplay']['body'];
export type BackofficeVoucherUpdateDisplayResponse =
  VoucherCrud['updateDisplay']['response'];

export type BackofficeVoucherDeleteRequest = VoucherCrud['delete']['body'];
export type BackofficeVoucherDeleteResponse = VoucherCrud['delete']['response'];

export type BackofficeVoucherReorderRequest =
  VoucherCrud['reorderVoucher']['body'];
export type BackofficeVoucherReorderResponse =
  VoucherCrud['reorderVoucher']['response'];

export type BackofficeVoucherUploadShipToCsvResponse =
  VoucherCrud['uploadShipToCsv']['response'];

export type BackofficeVoucherUploadBannerResponse =
  VoucherCrud['uploadBanner']['response'];

export type VoucherDeletePayload = BackofficeVoucherDeleteRequest;
export type VoucherDeleteApiResponse = BackofficeVoucherDeleteResponse;

// ---------------------------------------------------------------------------
// Common voucher status and shared types
// ---------------------------------------------------------------------------

export type VoucherStatus = VoucherListItem['status'];

// API-aligned types based on the voucher creation endpoint
export type VoucherTargetType = VoucherTargetConfigurationSchema['target_type'];
export type VoucherAcquisitionType =
  VoucherAcquisitionConfigurationSchema['acquisition_type'];
export type VoucherConditionType =
  VoucherConditionConfigurationSchema['condition_type'];
export type VoucherRewardType = VoucherRewardConfigurationSchema['reward_type'];

/**
 * Constants for VoucherRewardType values
 * Use these instead of magic strings for type-safe comparisons
 */
export const VOUCHER_REWARD_TYPE: {
  readonly DISCOUNT_PERCENT: 'DISCOUNT_PERCENT';
  readonly DISCOUNT_AMOUNT: 'DISCOUNT_AMOUNT';
  readonly FREE_GOODS: 'FREE_GOODS';
} = {
  DISCOUNT_PERCENT: 'DISCOUNT_PERCENT',
  DISCOUNT_AMOUNT: 'DISCOUNT_AMOUNT',
  FREE_GOODS: 'FREE_GOODS',
};

export type VoucherMinQuantityType = 'PER_ITEM' | 'ALL_ITEM';

export type VoucherRewardRuleProduct =
  VoucherRewardConfigurationSchema['reward_rule_products'][number];
export type VoucherDiscountProduct = NonNullable<
  VoucherRewardConfigurationSchema['discount_products']
>[number];
export type VoucherFreeGood = NonNullable<
  VoucherRewardConfigurationSchema['free_goods']
>[number];

export type VoucherTargetConfiguration = VoucherTargetConfigurationSchema;
export type VoucherAcquisitionConfiguration =
  VoucherAcquisitionConfigurationSchema;
export type VoucherConditionConfiguration = VoucherConditionConfigurationSchema;
export type VoucherRewardConfiguration = VoucherRewardConfigurationSchema;

export type CreateVoucherRequest = VoucherCreateBody;
export type UpdateVoucherRequest = VoucherUpdateBody;

// ---------------------------------------------------------------------------
// Buyer-facing voucher types (mobile / sol buyer)
// Derived from shared schema for proper type safety
// ---------------------------------------------------------------------------

export type VoucherEligibilityRequest = VoucherCrud['getEligibility']['body'];
export type VoucherEligibilityResponse =
  VoucherCrud['getEligibility']['response'];
export type VoucherEligibilityItem = VoucherEligibilityResponse['data'][number];

export type VoucherGetEligibilitiesRequest =
  VoucherCrud['getEligibilities']['query'];
export type VoucherGetEligibilitiesResponse =
  VoucherCrud['getEligibilities']['response'];
export type VoucherGetEligibilitiesItem =
  VoucherGetEligibilitiesResponse['data'][number];

export type VoucherGetByCodeRequest = VoucherCrud['getByCode']['body'];
export type VoucherGetByCodeResponse = VoucherCrud['getByCode']['response'];
export type VoucherValidateRequest = VoucherCrud['validate']['body'];
export type VoucherValidateResponse = VoucherCrud['validate']['response'];

export type VoucherCartItem = VoucherGetByCodeRequest['items'][number];

/**
 * Union type for buyer-facing voucher items that include allow_other_vouchers
 * and can participate in combination rules.
 */
export type VoucherWithAllowOther =
  | VoucherEligibilityItem
  | VoucherGetByCodeResponse;

// ---------------------------------------------------------------------------
// Buyer-facing voucher message types (extracted from VoucherGetByCodeResponse)
// Reusable utility types for handling voucher messaging
// ---------------------------------------------------------------------------

/** Extract reward message type from VoucherGetByCodeResponse (handles optional array) */
export type VoucherRewardMessage = Exclude<
  VoucherGetByCodeResponse['reward_messages'],
  undefined
>[number];

/** Extract term message type from VoucherGetByCodeResponse (handles optional array) */
export type VoucherTermMessage = Exclude<
  VoucherGetByCodeResponse['term_messages'],
  undefined
>[number];

/** Extract warning message type from VoucherGetByCodeResponse */
export type VoucherWarningMessage =
  VoucherGetByCodeResponse['warning_messages'][number];

/** Extract product info from message types */
export type VoucherMessageProduct = VoucherRewardMessage['products'][number];

// These are not yet covered by the shared schema, so we keep local definitions

export interface Voucher {
  id: string;
  code: string;
  title: string;
  description: string;
  image_url: string;
  start_date: string;
  end_date: string;
  enabled: boolean;
}

export interface VoucherListResponse {
  data: Voucher[];
  total: number;
}

export interface VoucherApplyResponse {
  success: boolean;
  message: string;
  voucher?: Voucher;
}
